import type { ReactNode } from "react"
import {
	IconCircleCheckFill18 as CircleCheck,
	IconTriangleWarningFill18 as Warning,
} from "nucleo-ui-fill-18"
import {
	IconBookOpen4Outline18 as BookIcon,
	IconXmarkOutline18 as XIcon,
} from "nucleo-ui-outline-18"

import { Button, IconButton, Loader } from "@productlane/ui"

import type { SiteImportBannerStatus } from "@/screens/app/helpCenter/site-import-banner"
import { isDevEnvironment } from "@/env"
import {
	SiteImportBannerPill,
	siteImportBannerText,
	siteImportHost,
} from "@/screens/app/helpCenter/site-import-banner"
import { NotFound } from "@/screens/not-found"

const RUNNING: SiteImportBannerStatus = {
	state: "running",
	url: "https://help.acme.com",
}

const DONE: SiteImportBannerStatus = {
	state: "done",
	url: "https://help.acme.com",
	importedCount: 84,
	imagesLeftBehind: 0,
	published: true,
}

const FAILED: SiteImportBannerStatus = {
	state: "failed",
	url: "https://help.acme.com",
	message: "Couldn't read help.acme.com. The site refused our reader.",
}

interface VariantProps {
	status: SiteImportBannerStatus
	onDismiss: () => void
}

function StateIcon({ state }: { state: SiteImportBannerStatus["state"] }) {
	if (state === "running") return <Loader className="h-3.5 w-3.5 flex-none" />
	if (state === "done")
		return <CircleCheck className="h-4 w-4 flex-none text-green-500" />
	return <Warning className="h-4 w-4 flex-none text-tertiary" />
}

// 1. Floating pill, centered at the bottom. The shipped shape.
export function SiteImportPill({ status, onDismiss }: VariantProps) {
	return (
		<div className="flex justify-center">
			<div className="flex h-9 max-w-full items-center gap-2 rounded-full bg-white pl-3.5 pr-1.5 text-[13px] shadow-lg ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10">
				<StateIcon state={status.state} />
				<span className="min-w-0 truncate text-secondary">
					{siteImportBannerText(status)}
				</span>
				{status.state === "done" && (
					<span className="flex-none rounded-full px-2 py-0.5 text-primary hover:bg-gray-150 dark:hover:bg-gray-800">
						Open
					</span>
				)}
				{status.state !== "running" && (
					<IconButton
						variant="ghost"
						size="sm"
						isRounded
						Icon={XIcon}
						aria-label="Dismiss import status"
						className="flex-none"
						onClick={onDismiss}
					/>
				)}
			</div>
		</div>
	)
}

// 2. Corner card: icon tile, headline, detail line, a real button.
export function SiteImportCard({ status, onDismiss }: VariantProps) {
	const host = siteImportHost(status.url)
	const headline =
		status.state === "running"
			? `Importing ${host}`
			: status.state === "done"
				? `${status.importedCount ?? 0} articles imported`
				: `Import stopped`

	return (
		<div className="flex justify-end">
			<div className="flex w-[380px] items-start gap-3 rounded-xl bg-white p-3 shadow-lg ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10">
				<span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
					{status.state === "running" ? (
						<Loader className="h-3.5 w-3.5 flex-none" />
					) : (
						<BookIcon className="h-4 w-4 flex-none text-secondary" />
					)}
				</span>
				<div className="min-w-0 flex-1">
					<div className="text-[13px] text-primary">{headline}</div>
					<div className="mt-0.5 text-xs text-secondary">
						{siteImportBannerText(status)}
					</div>
					{status.state === "done" && (
						<Button variant="outline" size="sm" className="mt-2.5">
							Open help center
						</Button>
					)}
				</div>
				{status.state !== "running" && (
					<IconButton
						variant="ghost"
						size="sm"
						Icon={XIcon}
						aria-label="Dismiss import status"
						className="flex-none"
						onClick={onDismiss}
					/>
				)}
			</div>
		</div>
	)
}

// 3. Full-width bar at the top of the app, the shape that shipped on docs only.
export function SiteImportTopBar({ status, onDismiss }: VariantProps) {
	return (
		<div className="flex h-10 w-full items-center gap-2.5 border-b border-gray-200 bg-gray-50 px-4 text-[13px] dark:border-gray-700/80 dark:bg-gray-850">
			<StateIcon state={status.state} />
			<span className="min-w-0 truncate text-secondary">
				{siteImportBannerText(status)}
			</span>
			{status.state !== "running" && (
				<IconButton
					variant="ghost"
					size="md"
					Icon={XIcon}
					aria-label="Dismiss import status"
					className="ml-auto flex-none"
					onClick={onDismiss}
				/>
			)}
		</div>
	)
}

// 4. Same bar in blue, the colour the roadmap list banner already uses for a step in flight.
export function SiteImportBlueBar({ status, onDismiss }: VariantProps) {
	return (
		<div className="flex h-10 w-full items-center gap-2.5 border-b border-blue-200 bg-blue-50/70 px-4 text-[13px] dark:border-blue-400/20 dark:bg-blue-400/10">
			{status.state === "running" ? (
				<Loader className="h-3.5 w-3.5 flex-none" />
			) : status.state === "done" ? (
				<CircleCheck className="h-4 w-4 flex-none text-blue-600 dark:text-blue-400" />
			) : (
				<Warning className="h-4 w-4 flex-none text-blue-600 dark:text-blue-400" />
			)}
			<span className="min-w-0 truncate text-blue-800 dark:text-blue-300">
				{siteImportBannerText(status)}
			</span>
			{status.state !== "running" && (
				<IconButton
					variant="ghost"
					size="md"
					Icon={XIcon}
					aria-label="Dismiss import status"
					className="ml-auto flex-none"
					onClick={onDismiss}
				/>
			)}
		</div>
	)
}

// 5. The pill in brand green, the shipped shape.
export function SiteImportGreenPill({ status, onDismiss }: VariantProps) {
	return (
		<div className="flex justify-center">
			<SiteImportBannerPill status={status} onDismiss={onDismiss} />
		</div>
	)
}

export function SiteImportBannerDesignPage() {
	if (!isDevEnvironment) return <NotFound />

	const onDismiss = () => {
		console.log("dismiss")
	}

	return (
		<div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
			<div className="mx-auto max-w-[930px] px-6 py-10 pb-24">
				<div className="mb-8">
					<div className="text-lg font-semibold text-primary">
						Help center import banner, five options
					</div>
					<div className="mt-1 text-sm text-secondary">
						The banner now shows on every page of the app, so it has to hold
						still over any screen. Each option is drawn in all three states.
					</div>
				</div>
				<div className="space-y-12">
					<Variant
						label="1. Floating pill, bottom center, neutral"
						note="Sits over the app, so no screen changes height while an import runs."
						Component={SiteImportPill}
						onDismiss={onDismiss}
					/>
					<Variant
						label="2. Corner card"
						note="Headline plus detail, with a button into the help center. Takes more room, says more."
						Component={SiteImportCard}
						onDismiss={onDismiss}
					/>
					<Variant
						label="3. Full-width top bar"
						note="The docs-only shape, promoted to the whole app. Pushes every screen down by 40px."
						Component={SiteImportTopBar}
						onDismiss={onDismiss}
					/>
					<Variant
						label="4. Top bar in blue"
						note="The colour the roadmap list banner uses for work in flight. Reads as a step, not an error."
						Component={SiteImportBlueBar}
						onDismiss={onDismiss}
					/>
					<Variant
						label="5. Pill in brand green"
						note="Same geometry as option 1, tinted with the brand green. Shipped."
						Component={SiteImportGreenPill}
						onDismiss={onDismiss}
					/>
				</div>
			</div>
		</div>
	)
}

function Variant({
	label,
	note,
	Component,
	onDismiss,
}: {
	label: string
	note: string
	Component: (props: VariantProps) => ReactNode
	onDismiss: () => void
}) {
	return (
		<div className="space-y-2">
			<div className="text-xs font-medium text-primary">{label}</div>
			<div className="text-xxs text-tertiary">{note}</div>
			<div className="space-y-6 rounded-lg bg-white p-6 ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
				<Component status={RUNNING} onDismiss={onDismiss} />
				<Component status={DONE} onDismiss={onDismiss} />
				<Component status={FAILED} onDismiss={onDismiss} />
			</div>
		</div>
	)
}
